import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import JuntoApiService, { Instructor } from '../services/api';
import EntityActionButtons from '../components/EntityActionButtons/EntityActionButtons';

const InstructorsPage: React.FC = () => {
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState<Instructor | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    specialties: [] as string[],
    certifications: [] as string[],
    years_experience: '',
    instagram: '',
    website: '',
  });

  const loadInstructors = useCallback(async () => {
    try {
      setLoading(true);
      const data = await JuntoApiService.getInstructors(includeInactive);
      setInstructors(data);
      setError(null);
    } catch (err) {
      console.error('Failed to load instructors:', err);
      setError('Failed to load instructors');
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    loadInstructors();
  }, [loadInstructors]);

  const openCreateDialog = () => {
    setEditingInstructor(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      bio: '',
      specialties: [],
      certifications: [],
      years_experience: '',
      instagram: '',
      website: '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const openEditDialog = (instructor: Instructor) => {
    setEditingInstructor(instructor);
    setFormData({
      name: instructor.name,
      email: instructor.email,
      phone: instructor.phone || '',
      bio: instructor.bio || '',
      specialties: instructor.specialties || [],
      certifications: instructor.certifications || [],
      years_experience: instructor.years_experience?.toString() || '',
      instagram: instructor.instagram || '',
      website: instructor.website || '',
    });
    setDialogError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingInstructor(null);
    setDialogError(null);
  };

  const handleSave = async () => {
    try {
      setDialogError(null);
      
      const instructorData = {
        ...formData,
        id: editingInstructor?.id || `instructor-${Date.now()}`,
        years_experience: formData.years_experience ? parseInt(formData.years_experience) : undefined,
        created_at: editingInstructor?.created_at || new Date().toISOString(),
      };

      if (editingInstructor) {
        // Update existing instructor
        await JuntoApiService.updateInstructor(instructorData as Instructor);
      } else {
        // Create new instructor
        await JuntoApiService.createInstructor(instructorData);
      }
      
      await loadInstructors();
      closeDialog();
    } catch (err) {
      console.error('Failed to save instructor:', err);
      setDialogError('Failed to save instructor. Please check your input.');
    }
  };


  const handleSpecialtyChange = (specialty: string) => {
    const currentSpecialties = formData.specialties;
    if (currentSpecialties.includes(specialty)) {
      setFormData({
        ...formData,
        specialties: currentSpecialties.filter(s => s !== specialty)
      });
    } else {
      setFormData({
        ...formData,
        specialties: [...currentSpecialties, specialty]
      });
    }
  };

  const handleCertificationAdd = (cert: string) => {
    if (cert.trim() && !formData.certifications.includes(cert.trim())) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, cert.trim()]
      });
    }
  };

  const handleCertificationRemove = (index: number) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading instructors...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Instructors</Typography>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                icon={<VisibilityOffIcon />}
                checkedIcon={<VisibilityIcon />}
              />
            }
            label="Show Inactive"
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Instructor
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
          <Button size="small" onClick={loadInstructors} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Specialties</TableCell>
                <TableCell>Experience</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instructors.map((instructor) => (
                <TableRow 
                  key={instructor.id} 
                  hover 
                  sx={{ 
                    opacity: instructor.is_active !== false ? 1 : 0.6,
                    backgroundColor: instructor.is_active !== false ? 'inherit' : 'action.hover'
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <PersonIcon color={instructor.is_active !== false ? "primary" : "disabled"} sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {instructor.name}
                        </Typography>
                        {instructor.bio && (
                          <Typography variant="body2" color="text.secondary">
                            {instructor.bio.length > 60 ? `${instructor.bio.substring(0, 60)}...` : instructor.bio}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <EmailIcon color="action" sx={{ mr: 1 }} />
                      {instructor.email}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(instructor.specialties || []).map((specialty) => (
                      <Chip
                        key={specialty}
                        label={specialty}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    {instructor.years_experience ? `${instructor.years_experience} years` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={instructor.is_active !== false ? 'Active' : 'Inactive'}
                      color={instructor.is_active !== false ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <EntityActionButtons
                      entity={instructor}
                      entityType="instructors"
                      entityDisplayName="Instructor"
                      onEdit={openEditDialog}
                      onRefresh={loadInstructors}
                      renderEntityInHistory={(entity) => (
                        <Box>
                          <Typography variant="body1">
                            <strong>Name:</strong> {entity.name}
                          </Typography>
                          <Typography variant="body1">
                            <strong>Email:</strong> {entity.email}
                          </Typography>
                          {entity.phone && (
                            <Typography variant="body1">
                              <strong>Phone:</strong> {entity.phone}
                            </Typography>
                          )}
                          {entity.bio && (
                            <Typography variant="body1">
                              <strong>Bio:</strong> {entity.bio}
                            </Typography>
                          )}
                          {entity.years_experience && (
                            <Typography variant="body1">
                              <strong>Experience:</strong> {entity.years_experience} years
                            </Typography>
                          )}
                          <Typography variant="body1">
                            <strong>Active:</strong> {entity.is_active ? 'Yes' : 'No'}
                          </Typography>
                        </Box>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingInstructor ? 'Edit Instructor' : 'Add New Instructor'}
        </DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Years of Experience"
                type="number"
                value={formData.years_experience}
                onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Instagram Handle"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                placeholder="@username"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Specialties
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {['yoga', 'pilates', 'reformer', 'mat', 'barre', 'meditation'].map((specialty) => (
                  <Chip
                    key={specialty}
                    label={specialty}
                    onClick={() => handleSpecialtyChange(specialty)}
                    color={formData.specialties.includes(specialty) ? 'primary' : 'default'}
                    variant={formData.specialties.includes(specialty) ? 'filled' : 'outlined'}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Certifications
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                {formData.certifications.map((cert, index) => (
                  <Chip
                    key={index}
                    label={cert}
                    onDelete={() => handleCertificationRemove(index)}
                    size="small"
                    color="secondary"
                  />
                ))}
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder="Add certification and press Enter"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const target = e.target as HTMLInputElement;
                    handleCertificationAdd(target.value);
                    target.value = '';
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            {editingInstructor ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstructorsPage;